import { authenticationAppRoutes } from "@circle-react/components/OnboardingPage/helpers/routes";
import { ApiError } from "@circle-react/config/CustomErrors";
import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import type { Dispatch, SetStateAction } from "react";
import type { UseMutationOptions } from "react-query";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

interface ResetPasswordResult {
  redirect_url: string;
}
interface ResetPasswordProps {
  // Endpoint to POST to. Defaults to the reset-password route so existing
  // callers (e.g. ResetPasswordForm) are unaffected. The OTP-first
  // "skip to password" flow opts into /auth/password_setup explicitly.
  endpoint?: string;
  options?: UseMutationOptions<any, unknown, any, unknown>;
  setIsResettingPassword: Dispatch<SetStateAction<boolean>>;
}

interface RequestBody {
  user: {
    password: string;
    password_confirmation: string;
    // Optional and ignored server-side: the backend's password_update_params
    // does not permit :email, so callers must not rely on it being applied.
    email?: string;
  };
}

export const useResetPassword = ({
  setIsResettingPassword,
  options = {},
  endpoint = authenticationAppRoutes.resetPasswordOnOtpLogin(),
}: ResetPasswordProps) => {
  const toast = useToast();
  // Plain window.location check rather than useLocation(): CreatePasswordStep
  // (the only caller that needs this) can render outside a <RouterProvider>
  // (e.g. the sign-in OTP flow), where useLocation() throws.
  const isDiscoverSignUp = window.location.pathname.startsWith(
    authenticationAppRoutes.discoverSignUp(),
  );

  return useMutation({
    ...options,
    mutationFn: (body: RequestBody) => {
      setIsResettingPassword(true);
      return reactQueryPost(endpoint, {
        ...body,
        ...(isDiscoverSignUp && { source: "discover" }),
      });
    },
    onSuccess: (data: ResetPasswordResult, ...args) => {
      if (!data?.redirect_url) {
        setIsResettingPassword(false);
        toast.error(t("otp_challenge_modal.generic_error"));
        return;
      }
      options.onSuccess?.(data, ...args);
      // No success toast: window.location.href triggers a full-page
      // navigation that tears down this DOM before the toast can render, so it
      // would never be seen. Mirrors Auth::OtpConfirmationsController, which
      // also shows nothing client-side on success.
      window.location.href = data.redirect_url;
    },
    onError: (error: unknown, ...args) => {
      setIsResettingPassword(false);
      options.onError?.(error, ...args);
      console.error(error);
      // On a 422 the server returns an actionable validation message
      // (e.g. password rules that diverge from the client regex) in
      // error.body.message — surface it so the user can correct the input.
      // For any other status (e.g. the 401 "Unauthorized" JSON from
      // ensure_passwordless_signup) fall back to a friendly generic message.
      const serverMessage =
        error instanceof ApiError && error.status === 422
          ? error.body?.message
          : undefined;
      toast.error(
        typeof serverMessage === "string" && serverMessage.length > 0
          ? serverMessage
          : t("otp_challenge_modal.generic_error"),
      );
    },
  });
};
